import React, { CSSProperties, MouseEventHandler } from 'react';
import classNames from 'classnames';
import SurfHeight from 'components/SurfHeight';
import { CamIcon } from 'components/Icons';
import type { SurfHeight as SurfHeightType } from 'types/units';

import styles from './SpotMarker.module.scss';

interface Surf {
  min: number;
  max: number;
  units: string;
  plus?: boolean;
}

interface Style {
  camera?: CSSProperties;
  container?: CSSProperties;
  main?: CSSProperties;
  surf?: CSSProperties;
}

interface QuiverSpotMarkerProps {
  surf: Surf;
  conditions?: string;
  hasCamera?: boolean;
  highlight?: boolean;
  style?: Style;
}

const containerClassName = (conditions: string, highlight: boolean): string => {
  const transformedConditions = conditions.replace(/_/g, '-').toLowerCase();
  return classNames(
    'quiver-spot-marker__container',
    `quiver-spot-marker__container--${transformedConditions}`,
    { 'quiver-spot-marker__container--highlight': highlight },
  );
};

const QuiverSpotMarker: React.FC<QuiverSpotMarkerProps> = ({
  conditions = 'LOLA',
  surf: { min, max, units, plus },
  hasCamera = false,
  highlight = false,
  style,
}) => (
  <div className={classNames(styles.spotMarkerQuiver, 'quiver-spot-marker')}>
    <div className={containerClassName(conditions, highlight)} style={style?.container}>
      <div className="quiver-spot-marker__main" style={style?.main}>
        <div className="quiver-spot-marker__surf" style={style?.surf}>
          <SurfHeight min={min} max={max} units={units as SurfHeightType} plus={plus} />
        </div>
        {hasCamera ? (
          <div className="quiver-spot-marker__camera" style={style && style.camera}>
            <CamIcon />
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

interface SpotMarkerProps extends QuiverSpotMarkerProps {
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  onClick?: MouseEventHandler;
  current?: boolean;
}

const getClassName = (current?: boolean) =>
  classNames({
    [styles.spotMarker]: true,
    'sl-spot-marker': true,
    'sl-spot-marker--current': current,
  });

const SpotMarker: React.FC<SpotMarkerProps> = ({
  onMouseEnter = () => null,
  onMouseLeave = () => null,
  onClick = () => null,
  current = false,
  ...props
}) => (
  <div
    data-testid="spot-marker"
    className={getClassName(current)}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    role="button"
    tabIndex={0}
  >
    <QuiverSpotMarker {...props} />
  </div>
);

export default SpotMarker;
